import ks from "@shared/kalmstromShared4";
import { IListDefinition, IFieldDefinition } from "./DBInterfaces";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBListManager from "./DBListManager";

export default class DBCanvas {

    public static UpdateCanvasSize = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("listsContainer");
            if (container === null) { return; }

            let maxRight: number = 0;
            let maxBottom: number = 0;

            const MeasureList = (list: IListDefinition): void => {
                const x: number = list.Position !== null && list.Position !== undefined ? list.Position.X : 40;
                const y: number = list.Position !== null && list.Position !== undefined ? list.Position.Y : 40;
                const cardWidth: number = list.Collapsed ? 250 : 520;
                const cardElement: HTMLElement | null = document.getElementById("card_" + list.Id);
                const cardHeight: number = cardElement !== null ? cardElement.offsetHeight : 400;
                maxRight = Math.max(maxRight, x + cardWidth + 80);
                maxBottom = Math.max(maxBottom, y + cardHeight + 80);
            };
            DBState.Lists.forEach(MeasureList);

            container.style.minWidth = maxRight + "px";
            container.style.minHeight = maxBottom + "px";

            const svg: HTMLElement | null = document.getElementById("relationshipsCanvas");
            if (svg !== null) {
                svg.style.width = maxRight + "px";
                svg.style.height = maxBottom + "px";
            }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.UpdateCanvasSize");
        }
    };

    public static SetupCardDragging = (card: HTMLElement, listId: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const toolbar: Element | null = card.querySelector(".list-toolbar");
            if (toolbar === null) { return; }

            const HandleCardClick = (e: Event): void => {
                const target: HTMLElement = e.target as HTMLElement;
                if (target.tagName !== "BUTTON" && target.tagName !== "INPUT") {
                    DBListManager.SelectList(listId);
                }
            };
            card.addEventListener("click", HandleCardClick);

            const HandleToolbarMouseDown = (e: MouseEvent): void => {
                const target: HTMLElement = e.target as HTMLElement;
                if (target.tagName === "BUTTON" || target.closest("button")) { return; }

                const rect: DOMRect = card.getBoundingClientRect();
                DBState.DraggedCard = card;
                DBState.DragOffset.X = e.clientX - rect.left;
                DBState.DragOffset.Y = e.clientY - rect.top;
                card.classList.add("dragging");
                (toolbar as HTMLElement).style.cursor = "grabbing";
                e.preventDefault();
            };
            toolbar.addEventListener("mousedown", HandleToolbarMouseDown as EventListener);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.SetupCardDragging");
        }
    };

    public static HandleMouseMove = (e: MouseEvent): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.DraggedCard === null) { return; }

            const panel: HTMLElement | null = document.getElementById("canvasPanel");
            if (panel === null) { return; }
            const panelRect: DOMRect = panel.getBoundingClientRect();

            let x: number = e.clientX - panelRect.left - DBState.DragOffset.X + panel.scrollLeft;
            let y: number = e.clientY - panelRect.top - DBState.DragOffset.Y + panel.scrollTop;
            x = Math.max(0, x);
            y = Math.max(0, y);

            DBState.DraggedCard.style.left = x + "px";
            DBState.DraggedCard.style.top = y + "px";

            const listId: string = DBState.DraggedCard.id.replace("card_", "");
            const list: IListDefinition | null = DBState.FindList(listId);
            if (list !== null) {
                list.Position = { X: x, Y: y };
            }

            DBCanvas.UpdateCanvasSize();
            DBCanvas.DrawRelationships();
        } catch (e2) {
            ks.kGlobalErrorHandler(e2, "DBCanvas.HandleMouseMove");
        }
    };

    public static HandleMouseUp = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.DraggedCard === null) { return; }

            DBState.DraggedCard.classList.remove("dragging");
            const toolbar: Element | null = DBState.DraggedCard.querySelector(".list-toolbar");
            if (toolbar !== null) { (toolbar as HTMLElement).style.cursor = ""; }
            DBState.DraggedCard = null;
            DBState.SaveState();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.HandleMouseUp");
        }
    };

    public static DrawRelationships = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const svg: SVGElement | null = document.getElementById("relationshipsCanvas") as unknown as SVGElement;
            if (svg === null) { return; }
            svg.innerHTML = "";

            const panel: HTMLElement | null = document.getElementById("canvasPanel");
            if (panel === null) { return; }
            const panelRect: DOMRect = panel.getBoundingClientRect();
            const zoom: number = parseFloat(panel.dataset.zoom || "1");

            DBCanvas.AppendSvgDefs(svg, zoom);

            const DrawForList = (sourceList: IListDefinition): void => {
                const entries: [string, IFieldDefinition][] = Object.entries(sourceList.Properties || {}) as [string, IFieldDefinition][];
                const DrawForField = (entry: [string, IFieldDefinition]): void => {
                    DBCanvas.DrawSingleRelationship(svg, panel, panelRect, zoom, sourceList, entry[0], entry[1]);
                };
                entries.forEach(DrawForField);
            };
            DBState.Lists.forEach(DrawForList);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.DrawRelationships");
        }
    };

    private static AppendSvgDefs = (svg: SVGElement, zoom: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const defs: SVGDefsElement = document.createElementNS("http://www.w3.org/2000/svg", "defs");

            const gradient: SVGLinearGradientElement = document.createElementNS("http://www.w3.org/2000/svg", "linearGradient");
            gradient.setAttribute("id", "lineGradient");

            const stop1: SVGStopElement = document.createElementNS("http://www.w3.org/2000/svg", "stop");
            stop1.setAttribute("offset", "0%");
            stop1.setAttribute("style", "stop-color:#3b82f6;stop-opacity:1");
            const stop2: SVGStopElement = document.createElementNS("http://www.w3.org/2000/svg", "stop");
            stop2.setAttribute("offset", "50%");
            stop2.setAttribute("style", "stop-color:#6366f1;stop-opacity:1");
            const stop3: SVGStopElement = document.createElementNS("http://www.w3.org/2000/svg", "stop");
            stop3.setAttribute("offset", "100%");
            stop3.setAttribute("style", "stop-color:#8b5cf6;stop-opacity:1");

            gradient.appendChild(stop1);
            gradient.appendChild(stop2);
            gradient.appendChild(stop3);
            defs.appendChild(gradient);

            const marker: SVGMarkerElement = document.createElementNS("http://www.w3.org/2000/svg", "marker");
            marker.setAttribute("id", "arrowhead");
            marker.setAttribute("markerWidth", (12 * zoom).toString());
            marker.setAttribute("markerHeight", (12 * zoom).toString());
            marker.setAttribute("refX", (11 * zoom).toString());
            marker.setAttribute("refY", (6 * zoom).toString());
            marker.setAttribute("orient", "auto");

            const arrowSize: number = 12 * zoom;
            const polygon: SVGPolygonElement = document.createElementNS("http://www.w3.org/2000/svg", "polygon");
            polygon.setAttribute("points", "0 0, " + arrowSize + " " + (arrowSize / 2) + ", 0 " + arrowSize);
            polygon.setAttribute("fill", "#8b5cf6");
            marker.appendChild(polygon);
            defs.appendChild(marker);

            svg.appendChild(defs);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.AppendSvgDefs");
        }
    };

    private static DrawSingleRelationship = (svg: SVGElement, panel: HTMLElement, panelRect: DOMRect, zoom: number, sourceList: IListDefinition, fieldName: string, field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (field.Type !== "lookup" || !field.LookupList) { return; }

            const FindTarget = (l: IListDefinition): boolean => { return l.Title === field.LookupList; };
            const targetList: IListDefinition | undefined = DBState.Lists.find(FindTarget);
            if (targetList === undefined) { return; }

            const sourceCard: HTMLElement | null = document.getElementById("card_" + sourceList.Id);
            const targetCard: HTMLElement | null = document.getElementById("card_" + targetList.Id);
            if (sourceCard === null || targetCard === null) { return; }
            if (sourceCard.style.display === "none" || targetCard.style.display === "none") { return; }

            const coords: { x1: number; y1: number; x2: number; y2: number } = DBCanvas.CalculateConnectionPoints(sourceCard, targetCard, panel, panelRect);
            DBCanvas.RenderRelationshipLine(svg, zoom, coords, sourceList, targetList, sourceCard, targetCard, fieldName, field);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.DrawSingleRelationship");
        }
    };

    private static CalculateConnectionPoints = (sourceCard: HTMLElement, targetCard: HTMLElement, panel: HTMLElement, panelRect: DOMRect): { x1: number; y1: number; x2: number; y2: number } => {
        if (ks.kErrorMode === true) { return { x1: 0, y1: 0, x2: 0, y2: 0 }; }
        try {
            const sourceRect: DOMRect = sourceCard.getBoundingClientRect();
            const targetRect: DOMRect = targetCard.getBoundingClientRect();

            const sourceCenterX: number = sourceRect.left + sourceRect.width / 2;
            const sourceCenterY: number = sourceRect.top + sourceRect.height / 2;
            const targetCenterX: number = targetRect.left + targetRect.width / 2;
            const targetCenterY: number = targetRect.top + targetRect.height / 2;

            const horizontalDist: number = Math.abs(targetCenterX - sourceCenterX);
            const verticalDist: number = Math.abs(targetCenterY - sourceCenterY);

            let x1: number; let y1: number; let x2: number; let y2: number;

            if (horizontalDist > verticalDist) {
                if (targetCenterX > sourceCenterX) {
                    x1 = sourceRect.right - panelRect.left + panel.scrollLeft;
                    y1 = sourceCenterY - panelRect.top + panel.scrollTop;
                    x2 = targetRect.left - panelRect.left + panel.scrollLeft;
                    y2 = targetCenterY - panelRect.top + panel.scrollTop;
                } else {
                    x1 = sourceRect.left - panelRect.left + panel.scrollLeft;
                    y1 = sourceCenterY - panelRect.top + panel.scrollTop;
                    x2 = targetRect.right - panelRect.left + panel.scrollLeft;
                    y2 = targetCenterY - panelRect.top + panel.scrollTop;
                }
            } else {
                if (targetCenterY > sourceCenterY) {
                    x1 = sourceCenterX - panelRect.left + panel.scrollLeft;
                    y1 = sourceRect.bottom - panelRect.top + panel.scrollTop;
                    x2 = targetCenterX - panelRect.left + panel.scrollLeft;
                    y2 = targetRect.top - panelRect.top + panel.scrollTop;
                } else {
                    x1 = sourceCenterX - panelRect.left + panel.scrollLeft;
                    y1 = sourceRect.top - panelRect.top + panel.scrollTop;
                    x2 = targetCenterX - panelRect.left + panel.scrollLeft;
                    y2 = targetRect.bottom - panelRect.top + panel.scrollTop;
                }
            }

            return { x1: x1, y1: y1, x2: x2, y2: y2 };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.CalculateConnectionPoints");
            return { x1: 0, y1: 0, x2: 0, y2: 0 };
        }
    };

    private static RenderRelationshipLine = (svg: SVGElement, zoom: number, coords: { x1: number; y1: number; x2: number; y2: number }, sourceList: IListDefinition, targetList: IListDefinition, sourceCard: HTMLElement, targetCard: HTMLElement, fieldName: string, field: IFieldDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const group: SVGGElement = document.createElementNS("http://www.w3.org/2000/svg", "g");
            group.setAttribute("class", "relationship-group");

            const dx: number = coords.x2 - coords.x1;
            const dy: number = coords.y2 - coords.y1;
            const dist: number = Math.sqrt(dx * dx + dy * dy);
            const horizontalDist: number = Math.abs(dx);
            const verticalDist: number = Math.abs(dy);

            const cp: { cp1x: number; cp1y: number; cp2x: number; cp2y: number } = DBCanvas.CalculateControlPoints(coords, horizontalDist, verticalDist, dist, dx, dy);
            const path: SVGPathElement = DBCanvas.CreateRelationshipPath(zoom, coords, cp, sourceList, targetList, sourceCard, targetCard, fieldName, field, group);
            group.appendChild(path);

            DBCanvas.AddRelationshipLabel(group, svg, zoom, coords, cp, fieldName, sourceList, targetList, field, path);
            svg.appendChild(group);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.RenderRelationshipLine");
        }
    };

    private static CalculateControlPoints = (coords: { x1: number; y1: number; x2: number; y2: number }, horizontalDist: number, verticalDist: number, dist: number, dx: number, dy: number): { cp1x: number; cp1y: number; cp2x: number; cp2y: number } => {
        if (ks.kErrorMode === true) { return { cp1x: 0, cp1y: 0, cp2x: 0, cp2y: 0 }; }
        try {
            if (horizontalDist > verticalDist) {
                const curveOffset: number = Math.min(dist * 0.4, 150);
                return {
                    cp1x: coords.x1 + (dx > 0 ? curveOffset : -curveOffset),
                    cp1y: coords.y1,
                    cp2x: coords.x2 - (dx > 0 ? curveOffset : -curveOffset),
                    cp2y: coords.y2
                };
            }
            const curveOffset: number = Math.min(dist * 0.4, 120);
            return {
                cp1x: coords.x1,
                cp1y: coords.y1 + (dy > 0 ? curveOffset : -curveOffset),
                cp2x: coords.x2,
                cp2y: coords.y2 - (dy > 0 ? curveOffset : -curveOffset)
            };
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.CalculateControlPoints");
            return { cp1x: 0, cp1y: 0, cp2x: 0, cp2y: 0 };
        }
    };

    private static CreateRelationshipPath = (zoom: number, coords: { x1: number; y1: number; x2: number; y2: number }, cp: { cp1x: number; cp1y: number; cp2x: number; cp2y: number }, sourceList: IListDefinition, targetList: IListDefinition, sourceCard: HTMLElement, targetCard: HTMLElement, fieldName: string, field: IFieldDefinition, group: SVGGElement): SVGPathElement => {
        if (ks.kErrorMode === true) { return document.createElementNS("http://www.w3.org/2000/svg", "path"); }
        try {
            const path: SVGPathElement = document.createElementNS("http://www.w3.org/2000/svg", "path");
            const d: string = "M " + coords.x1 + " " + coords.y1 + " C " + cp.cp1x + " " + cp.cp1y + ", " + cp.cp2x + " " + cp.cp2y + ", " + coords.x2 + " " + coords.y2;

            path.setAttribute("d", d);
            path.setAttribute("stroke", "#3b82f6");
            path.setAttribute("stroke-width", (4 * zoom).toString());
            path.setAttribute("fill", "none");
            path.setAttribute("stroke-dasharray", (10 * zoom) + " " + (5 * zoom));
            path.setAttribute("marker-end", "url(#arrowhead)");
            path.setAttribute("class", "relationship-line");
            path.setAttribute("stroke-linecap", "round");
            path.setAttribute("opacity", "0.9");
            path.style.cursor = "pointer";
            path.style.transition = "all 0.2s";

            const HandleEnter = (): void => {
                path.setAttribute("stroke-width", (6 * zoom).toString());
                path.setAttribute("opacity", "1");
                group.setAttribute("data-hover", "true");
                sourceCard.style.boxShadow = "0 0 20px rgba(59, 130, 246, 0.6)";
                targetCard.style.boxShadow = "0 0 20px rgba(139, 92, 246, 0.6)";
            };
            const HandleLeave = (): void => {
                path.setAttribute("stroke-width", (4 * zoom).toString());
                path.setAttribute("opacity", "0.9");
                group.removeAttribute("data-hover");
                sourceCard.style.boxShadow = "";
                targetCard.style.boxShadow = "";
            };
            const HandleClick = (): void => {
                DBCanvas.ShowRelationshipDetails(sourceList.Title, targetList.Title, field.Title || fieldName, field.LookupField || "Title");
            };
            path.addEventListener("mouseenter", HandleEnter);
            path.addEventListener("mouseleave", HandleLeave);
            path.addEventListener("click", HandleClick);

            return path;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.CreateRelationshipPath");
            return document.createElementNS("http://www.w3.org/2000/svg", "path");
        }
    };

    private static AddRelationshipLabel = (group: SVGGElement, svg: SVGElement, zoom: number, coords: { x1: number; y1: number; x2: number; y2: number }, cp: { cp1x: number; cp1y: number; cp2x: number; cp2y: number }, fieldName: string, sourceList: IListDefinition, targetList: IListDefinition, field: IFieldDefinition, path: SVGPathElement): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const t: number = 0.5;
            const mt: number = 1 - t;
            const labelX: number = mt * mt * mt * coords.x1 + 3 * mt * mt * t * cp.cp1x + 3 * mt * t * t * cp.cp2x + t * t * t * coords.x2;
            const labelY: number = mt * mt * mt * coords.y1 + 3 * mt * mt * t * cp.cp1y + 3 * mt * t * t * cp.cp2y + t * t * t * coords.y2;

            const labelText: string = fieldName.length > 12 ? fieldName.substring(0, 11) + "\u2026" : fieldName;
            const labelWidth: number = Math.max(labelText.length * 7 + 16, 60);

            const labelBg: SVGRectElement = document.createElementNS("http://www.w3.org/2000/svg", "rect");
            labelBg.setAttribute("x", (labelX - labelWidth / 2).toString());
            labelBg.setAttribute("y", (labelY - 12).toString());
            labelBg.setAttribute("width", labelWidth.toString());
            labelBg.setAttribute("height", "24");
            labelBg.setAttribute("rx", (12 * zoom).toString());
            labelBg.setAttribute("fill", "#ffffff");
            labelBg.setAttribute("stroke", "#3b82f6");
            labelBg.setAttribute("stroke-width", (2 * zoom).toString());
            labelBg.setAttribute("class", "relationship-label-bg");
            labelBg.style.cursor = "pointer";
            labelBg.style.transition = "all 0.2s";

            const label: SVGTextElement = document.createElementNS("http://www.w3.org/2000/svg", "text");
            label.setAttribute("x", labelX.toString());
            label.setAttribute("y", (labelY + 4 * zoom).toString());
            label.setAttribute("text-anchor", "middle");
            label.setAttribute("fill", "#1e40af");
            label.setAttribute("font-size", (11 * zoom).toString());
            label.setAttribute("font-family", "Segoe UI, system-ui, sans-serif");
            label.setAttribute("font-weight", "600");
            label.textContent = "\u2197 " + labelText;
            label.style.cursor = "pointer";
            label.style.userSelect = "none";

            const title: SVGTitleElement = document.createElementNS("http://www.w3.org/2000/svg", "title");
            title.textContent = sourceList.Title + "." + (field.Title || fieldName) + " \u2192 " + targetList.Title + "." + (field.LookupField || "Title");
            group.appendChild(title);

            const HandleBgEnter = (): void => {
                labelBg.setAttribute("fill", "#dbeafe");
                path.setAttribute("stroke-width", (6 * zoom).toString());
                path.setAttribute("opacity", "1");
            };
            const HandleBgLeave = (): void => {
                labelBg.setAttribute("fill", "#ffffff");
                if (!group.hasAttribute("data-hover")) {
                    path.setAttribute("stroke-width", (4 * zoom).toString());
                    path.setAttribute("opacity", "0.9");
                }
            };
            const HandleLabelClick = (): void => {
                DBCanvas.ShowRelationshipDetails(sourceList.Title, targetList.Title, field.Title || fieldName, field.LookupField || "Title");
            };

            labelBg.addEventListener("mouseenter", HandleBgEnter);
            labelBg.addEventListener("mouseleave", HandleBgLeave);
            labelBg.addEventListener("click", HandleLabelClick);
            label.addEventListener("click", HandleLabelClick);

            group.appendChild(labelBg);
            group.appendChild(label);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.AddRelationshipLabel");
        }
    };

    public static ShowRelationshipDetails = (sourceListTitle: string, targetListTitle: string, fieldName: string, lookupField: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            DBModalManager.ShowToast("Lookup: " + sourceListTitle + "." + fieldName + " \u2192 " + targetListTitle + "." + lookupField, "success");
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.ShowRelationshipDetails");
        }
    };

    public static ClearRelationships = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const svg: HTMLElement | null = document.getElementById("relationshipsCanvas");
            if (svg !== null) { svg.innerHTML = ""; }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCanvas.ClearRelationships");
        }
    };
}
